import { CHAT_COPY, type ChatMessage } from '@/entities/chat'

export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024

const MIME_BY_EXTENSION: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  heic: 'image/heic',
  webp: 'image/webp',
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  txt: 'text/plain',
  csv: 'text/csv',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
}

export function mimeForFile(file: File): string | null {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? ''
  return MIME_BY_EXTENSION[extension] ?? null
}

export function isImageMime(contentType: string): boolean {
  return contentType.startsWith('image/')
}

export function newClientRef(): string {
  return crypto.randomUUID()
}

export type ConversationKind = 'client' | 'direct' | 'group'

export function conversationKind(type: string): ConversationKind {
  if (type === 'Direct') return 'direct'
  if (type === 'Group') return 'group'
  return 'client'
}

export function isInternalKind(kind: ConversationKind): boolean {
  return kind !== 'client'
}

export async function imageDimensions(file: File): Promise<{ width?: number; height?: number }> {
  if (!file.type.startsWith('image/')) return {}
  const url = URL.createObjectURL(file)
  try {
    const bitmap = await createImageBitmap(file)
    return { width: bitmap.width, height: bitmap.height }
  } catch {
    return {}
  } finally {
    URL.revokeObjectURL(url)
  }
}

export type DayGroup = { day: string; messages: ChatMessage[] }

export function groupByDay(messages: ChatMessage[]): DayGroup[] {
  const groups: DayGroup[] = []
  for (const message of messages) {
    const day = new Date(message.sentAt).toDateString()
    const last = groups[groups.length - 1]
    if (last && last.day === day) last.messages.push(message)
    else groups.push({ day, messages: [message] })
  }
  return groups
}

export function isSystemMessage(message: ChatMessage): boolean {
  return message.kind === 'System'
}

export function lastReadableMessageId(messages: ChatMessage[]): number | null {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (!isSystemMessage(messages[index])) return messages[index].id
  }
  return null
}

export function systemMessageText(message: ChatMessage): string | null {
  const system = message.content?.system
  if (!system) return null
  const actor = message.senderName
  switch (system.event) {
    case 'group_created':
      return CHAT_COPY.systemGroupCreated(actor)
    case 'group_renamed':
      return system.title ? CHAT_COPY.systemGroupRenamed(actor, system.title) : null
    case 'member_added':
      return system.targetName ? CHAT_COPY.systemMemberAdded(actor, system.targetName) : null
    case 'member_removed':
      return system.targetName ? CHAT_COPY.systemMemberRemoved(actor, system.targetName) : null
    case 'member_left':
      return CHAT_COPY.systemMemberLeft(actor)
    default:
      return null
  }
}
